/**
 * Line graph generator: computes every position (scales, ticks, labels, points, paths) from
 * the prepared data. Pure geometry, no SVG strings, so the web preview can reuse it for
 * interactive tooltips.
 */
import { GraphOptions } from '../options';
import {
    Bucketing,
    bucketDays,
    bucketLabel,
    bucketTitle,
    chooseBucketing,
    metricValues,
} from '../shared/aggregate';
import { MONTH_NAMES, formatLongDate, toUtc } from '../shared/dates';
import { formatNumber } from '../shared/escape';
import { buildCurve } from '../shared/curve';
import { niceScale } from '../shared/scale';
import { estimateTextWidth, round } from '../shared/svg';
import { PreparedData } from '../shared/types';

export const DEFAULT_LINE_WIDTH = 900;
export const DEFAULT_LINE_HEIGHT = 360;

/** Ranges up to this many days get day-number labels; longer ones get month labels. */
const SHORT_RANGE_DAYS = 62;

export { buildCurve, niceScale };

export interface LinePoint {
    x: number;
    y: number;
    /** Day, or first day of the week/month bucket. */
    date: string;
    count: number;
    /** e.g. `September 23, 2026`, `Week of Sep 7, 2026`, `September 2026` */
    title: string;
}

interface SeriesPoint {
    date: string;
    count: number;
    title: string;
    /** Axis label for grouped points (daily points use the date-based labels). */
    label?: string;
}

/** Daily values, or contribution totals per week/month when grouping is enabled. */
const buildSeries = (data: PreparedData, bucketing: Bucketing): SeriesPoint[] => {
    if (bucketing === 'day')
        return data.days.map((day) => ({
            date: day.date,
            count: day.count,
            title: formatLongDate(day.date),
        }));
    const buckets = bucketDays(data.days, bucketing);
    const totals = metricValues(buckets, 'contributions');
    const spansYears =
        buckets.length > 0 &&
        buckets[0].start.slice(0, 4) !== buckets[buckets.length - 1].start.slice(0, 4);
    return buckets.map((bucket, i) => ({
        date: bucket.start,
        count: totals[i],
        title: bucketTitle(bucket, bucketing),
        label: bucketLabel(bucket, bucketing, buckets[i - 1], spansYears),
    }));
};

export interface YTick {
    value: number;
    y: number;
}

export interface XLabel {
    x: number;
    text: string;
}

export interface LineLayout {
    width: number;
    height: number;
    plot: { left: number; right: number; top: number; bottom: number };
    title: { text: string; x: number; y: number; size: number } | null;
    total: { text: string; x: number; y: number } | null;
    yTicks: YTick[];
    xLabels: XLabel[];
    yAxisTitle: { x: number; y: number } | null;
    xAxisTitle: { x: number; y: number } | null;
    points: LinePoint[];
    bucketing: Bucketing;
    /** Radius actually drawn; 0 when points would overlap. */
    pointRadius: number;
    /** Indices of points whose value is printed above them. */
    valueIndices: number[];
    linePath: string;
    areaPath: string;
}

/** Grouped points: every k-th bucket label, anchored on the latest bucket. */
const buildBucketLabels = (
    points: LinePoint[],
    series: SeriesPoint[],
    plotWidth: number,
    fontSize: number,
): XLabel[] => {
    const n = points.length;
    const maxLabels = Math.max(2, Math.floor(plotWidth / (fontSize * 4.2)));
    const every = Math.ceil(n / maxLabels);
    return points.flatMap((point, i) =>
        (n - 1 - i) % every === 0 ? [{ x: point.x, text: series[i].label ?? '' }] : [],
    );
};

const buildXLabels = (points: LinePoint[], plotWidth: number, fontSize: number): XLabel[] => {
    const n = points.length;
    if (n === 0) return [];
    const maxLabels = Math.max(2, Math.floor(plotWidth / (fontSize * 4.2)));

    if (n <= SHORT_RANGE_DAYS) {
        // Every k-th day, anchored on the latest day so "today" is always labelled.
        const step = [1, 2, 3, 5, 7, 10, 14, 15].find((s) => Math.ceil(n / s) <= maxLabels) ?? 15;
        const indices: number[] = [];
        for (let i = n - 1; i >= 0; i -= step) indices.unshift(i);
        let previousMonth = -1;
        return indices.map((i) => {
            const date = new Date(toUtc(points[i].date));
            const month = date.getUTCMonth();
            const text =
                month !== previousMonth
                    ? `${MONTH_NAMES[month]} ${date.getUTCDate()}`
                    : String(date.getUTCDate());
            previousMonth = month;
            return { x: points[i].x, text };
        });
    }

    // Long ranges: one label per month, at the first of the month.
    const spansYears = points[0].date.slice(0, 4) !== points[n - 1].date.slice(0, 4);
    let labels: XLabel[] = points
        .filter((p) => p.date.endsWith('-01'))
        .map((p) => {
            const month = Number(p.date.slice(5, 7)) - 1;
            const text =
                spansYears && month === 0
                    ? `${MONTH_NAMES[month]} '${p.date.slice(2, 4)}`
                    : MONTH_NAMES[month];
            return { x: p.x, text };
        });
    if (labels.length > maxLabels) {
        const every = Math.ceil(labels.length / maxLabels);
        labels = labels.filter((_, i) => (labels.length - 1 - i) % every === 0);
    }
    return labels;
};

export const layoutLine = (data: PreparedData, options: GraphOptions): LineLayout => {
    const width = options.width || DEFAULT_LINE_WIDTH;
    const height = options.height || DEFAULT_LINE_HEIGHT;
    const fs = options.fontSize;
    const pad = options.padding;
    const titleSize = Math.round(fs * 1.5);

    const bucketing =
        options.groupBy === 'auto' ? chooseBucketing(data.days.length) : options.groupBy;
    const series = buildSeries(data, bucketing);
    const counts = series.map((point) => point.count);
    const scale = niceScale(Math.max(0, ...counts));

    // Header
    let cursor = pad;
    let title: LineLayout['title'] = null;
    let total: LineLayout['total'] = null;
    const titleText = options.title || `${data.name?.trim() || data.username}'s Contribution Graph`;
    if (options.showTitle) {
        cursor += titleSize;
        title = { text: titleText, x: width / 2, y: cursor, size: titleSize };
        cursor += Math.round(fs * 0.6);
    }
    if (options.showTotal) {
        cursor += Math.round(fs * 1.3);
        total = { text: data.totalText, x: width / 2, y: cursor };
    }
    if (title || total) cursor += Math.round(fs * 1.2);

    // Space the points and value labels need above the plot's top line.
    const markerRadius = options.showPoints ? options.pointRadius + options.pointBorderWidth : 0;
    const topRoom =
        Math.max(markerRadius, options.lineWidth) + (options.showValues ? fs * 1.5 : 0) + 4;

    const yTitleWidth = options.showAxisTitles ? Math.round(fs * 1.8) : 0;
    const yLabelWidth = options.showYLabels
        ? Math.round(estimateTextWidth(formatNumber(scale.max), fs) + fs * 0.8)
        : 0;
    const xLabelHeight = options.showXLabels ? Math.round(fs * 2) : 0;
    const xTitleHeight = options.showAxisTitles ? Math.round(fs * 1.8) : 0;
    const sideRoom = Math.max(markerRadius, options.lineWidth, options.showXLabels ? fs * 1.2 : 0);

    const plot = {
        left: pad + yTitleWidth + yLabelWidth + Math.max(markerRadius, 4),
        right: width - pad - sideRoom,
        top: cursor + topRoom,
        bottom: height - pad - xTitleHeight - xLabelHeight,
    };
    // Keep a usable plot even for tiny sizes.
    if (plot.right - plot.left < 20) plot.right = plot.left + 20;
    if (plot.bottom - plot.top < 20) plot.bottom = plot.top + 20;

    const plotWidth = plot.right - plot.left;
    const plotHeight = plot.bottom - plot.top;
    const n = series.length;
    const spacing = n > 1 ? plotWidth / (n - 1) : plotWidth;

    const points: LinePoint[] = series.map((point, i) => ({
        x: n > 1 ? plot.left + i * spacing : plot.left + plotWidth / 2,
        y: plot.bottom - (point.count / scale.max) * plotHeight,
        date: point.date,
        count: point.count,
        title: point.title,
    }));

    const yTicks: YTick[] = [];
    for (let value = 0; value <= scale.max; value += scale.step)
        yTicks.push({ value, y: plot.bottom - (value / scale.max) * plotHeight });

    // Shrink points on dense ranges; hide them when they would merge into a band.
    const pointRadius = options.showPoints
        ? Math.min(options.pointRadius, n > 1 ? spacing * 0.42 : options.pointRadius)
        : 0;

    let valueIndices: number[] = [];
    if (options.showValues && n > 0) {
        if (spacing >= fs * 2.2) {
            valueIndices = points.flatMap((p, i) => (p.count > 0 ? [i] : []));
        } else {
            const peak = Math.max(...counts);
            if (peak > 0) valueIndices = [counts.lastIndexOf(peak)];
        }
    }

    const linePath = buildCurve(points, options.smoothing / 100);
    const areaPath =
        points.length > 1
            ? `${linePath}L${round(points[n - 1].x)} ${round(plot.bottom)}L${round(points[0].x)} ${round(plot.bottom)}Z`
            : '';

    return {
        width,
        height,
        plot,
        title,
        total,
        yTicks,
        xLabels: !(options.showXLabels || options.showGridX)
            ? []
            : bucketing === 'day'
              ? buildXLabels(points, plotWidth, fs)
              : buildBucketLabels(points, series, plotWidth, fs),
        yAxisTitle: options.showAxisTitles
            ? { x: pad + fs * 0.9, y: plot.top + plotHeight / 2 }
            : null,
        xAxisTitle: options.showAxisTitles
            ? { x: plot.left + plotWidth / 2, y: height - pad - fs * 0.3 }
            : null,
        points,
        bucketing,
        pointRadius: pointRadius >= 1.5 ? pointRadius : 0,
        valueIndices,
        linePath,
        areaPath,
    };
};
